import type {
  User,
  Municipality,
  PlenarySession,
  AgendaItem,
  Document,
  Citation,
  ExtractedDataBlock,
  ReviewItem,
  SuggestedQuestion,
  Issue,
  Draft,
  ActivityEvent,
  Alert,
} from '@/types/models';

// ─── User ─────────────────────────────────────────────────────────────

export const mockUser: User = {
  id: 'user-001',
  nom: 'Marta',
  cognom: 'Serra',
  email: 'demo@plenari.cat',
  rol: 'regidor',
  municipiId: 'muni-001',
  avatarInitials: 'MS',
  grupMunicipal: 'Grup Municipal de Demostració',
  telefon: '600 000 000',
};

// ─── Municipalities ────────────────────────────────────────────────────
// NOTE: The old Municipality model is kept for backward compatibility with
// dashboardService. The canonical territorial model is in src/data/territorialCatalog.ts

export const mockMunicipalities: Municipality[] = [
  {
    id: 'muni-001',
    nom: 'Berga',
    comarca: 'Berguedà',
    provincia: 'Barcelona',
    poblacio: 14823,
    codiINE: '08022',
    web: 'https://www.berga.cat',
  },
  {
    id: 'muni-002',
    nom: 'Manresa',
    comarca: 'Bages',
    provincia: 'Barcelona',
    poblacio: 76250,
    codiINE: '08113',
    web: 'https://www.manresa.cat',
  },
  {
    id: 'muni-003',
    nom: 'Vic',
    comarca: 'Osona',
    provincia: 'Barcelona',
    poblacio: 47000,
    codiINE: '08298',
    web: 'https://www.vic.cat',
  },
];

// Keep singular export for backward compatibility
export const mockMunicipality: Municipality = mockMunicipalities[0];

// ─── Documents ────────────────────────────────────────────────────────

export const mockDocuments: Document[] = [
  // ── Berga General (ws-muni-001-general) ──────────────────────────
  {
    id: 'doc-001',
    nom: 'Convocatòria Ple Ordinari Agost 2026',
    tipus: 'convocatoria',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-28',
    pagines: 4,
    versio: '1.0',
  },
  {
    id: 'doc-002',
    nom: 'Proposta modificació pressupostària 2/2026',
    tipus: 'proposta',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-002',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-15',
    pagines: 12,
    versio: '1.1',
    alertes: ['Nova versió disponible des del 22/07/2026'],
  },
  {
    id: 'doc-003',
    nom: 'Annex econòmic modificació pressupostària',
    tipus: 'annex_economic',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-002',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-15',
    pagines: 8,
    versio: '1.0',
    alertes: ['Import a la pàg. 4 difereix de la proposta principal'],
  },
  {
    id: 'doc-004',
    nom: 'Ordenança reguladora de terrasses 2026',
    tipus: 'ordenanca',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-003',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-10',
    pagines: 24,
    versio: '2.3',
  },
  {
    id: 'doc-005',
    nom: 'Plec de condicions neteja viària 2026–2029',
    tipus: 'plec_condicions',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-004',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-08',
    pagines: 38,
    versio: '1.0',
  },
  {
    id: 'doc-006',
    nom: 'Informe tècnic adjudicació neteja viària',
    tipus: 'informe_tecnic',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-004',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-14',
    pagines: 6,
    versio: '1.0',
  },
  {
    id: 'doc-007',
    nom: 'Bases reguladores subvencions entitats 2026',
    tipus: 'proposta',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-005',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-12',
    pagines: 16,
    versio: '1.0',
  },
  {
    id: 'doc-008',
    nom: 'Conveni col·laboració Diputació de Barcelona',
    tipus: 'conveni',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-006',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-11',
    pagines: 10,
    versio: '1.0',
  },
  {
    id: 'doc-009',
    nom: 'Acta ple ordinari juny 2026',
    tipus: 'acta',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-001',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-02',
    pagines: 28,
    versio: '1.0',
  },
  {
    id: 'doc-010',
    nom: 'Memòria manteniment instal·lacions esportives 2025',
    tipus: 'memoria',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-007',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-09',
    pagines: 14,
    versio: '1.0',
    alertes: ['Informe tècnic complementari no consta en els documents disponibles'],
  },
  {
    id: 'doc-011',
    nom: 'Proposta ordenança residus municipals 2026',
    tipus: 'ordenanca',
    plenarySessionId: 'ple-002',
    workspaceId: 'ws-muni-001-general',
    data: '2026-05-20',
    pagines: 20,
    versio: '1.0',
  },
  {
    id: 'doc-012',
    nom: 'Convocatòria Ple Ordinari Setembre 2026',
    tipus: 'convocatoria',
    plenarySessionId: 'ple-003',
    workspaceId: 'ws-muni-001-general',
    data: '2026-07-28',
    pagines: 3,
    versio: '1.0',
  },
  // ── Berga AC (ws-muni-001-ac) ─────────────────────────────────────
  {
    id: 'doc-ac-001',
    nom: 'Convocatòria Ple Ordinari Agost 2026 — AC',
    tipus: 'convocatoria',
    plenarySessionId: 'ple-ac-001',
    workspaceId: 'ws-muni-001-ac',
    data: '2026-07-28',
    pagines: 4,
    versio: '1.0',
  },
  {
    id: 'doc-ac-002',
    nom: 'Informe intern — Seguiment actuacions municipals',
    tipus: 'informe_tecnic',
    plenarySessionId: 'ple-ac-001',
    agendaItemId: 'punt-ac-001',
    workspaceId: 'ws-muni-001-ac',
    data: '2026-07-20',
    pagines: 8,
    versio: '1.0',
  },
  // Manresa documents
  {
    id: 'doc-m-001',
    nom: 'Convocatòria Ple Ordinari Agost 2026 — Manresa',
    tipus: 'convocatoria',
    plenarySessionId: 'ple-m-001',
    workspaceId: 'ws-muni-002',
    data: '2026-07-25',
    pagines: 3,
    versio: '1.0',
  },
  // Vic documents
  {
    id: 'doc-v-001',
    nom: 'Convocatòria Ple Ordinari Setembre 2026 — Vic',
    tipus: 'convocatoria',
    plenarySessionId: 'ple-v-001',
    workspaceId: 'ws-muni-003',
    data: '2026-08-01',
    pagines: 3,
    versio: '1.0',
  },
];

// ─── Plenary Sessions ─────────────────────────────────────────────────

export const mockPlendarySessions: PlenarySession[] = [
  // ── Berga General (ws-muni-001-general) ──────────────────────────
  {
    id: 'ple-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'ordinari',
    status: 'en_preparacio',
    data: '2026-08-14',
    hora: '19:00',
    lloc: "Sala de Plens de l'Ajuntament de Berga",
    convocatoriaNom: 'Ple Ordinari — Agost 2026',
    totalPunts: 9,
    puntsRevisats: 3,
    preguntesPreparades: 5,
    alertesDocumentals: 3,
    percentatgeRevisio: 33,
    puntsIds: [
      'punt-001', 'punt-002', 'punt-003', 'punt-004',
      'punt-005', 'punt-006', 'punt-007', 'punt-008', 'punt-009',
    ],
    notes: "Ple amb punts de major rellevància pressupostària de l'any.",
  },
  {
    id: 'ple-002',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'ordinari',
    status: 'tancat',
    data: '2026-06-12',
    hora: '19:00',
    lloc: "Sala de Plens de l'Ajuntament de Berga",
    convocatoriaNom: 'Ple Ordinari — Juny 2026',
    totalPunts: 0,
    puntsRevisats: 0,
    preguntesPreparades: 0,
    alertesDocumentals: 0,
    percentatgeRevisio: 100,
    puntsIds: [],
    notes: '',
  },
  {
    id: 'ple-003',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'ordinari',
    status: 'convocatoria_rebuda',
    data: '2026-09-11',
    hora: '19:00',
    lloc: "Sala de Plens de l'Ajuntament de Berga",
    convocatoriaNom: 'Ple Ordinari — Setembre 2026',
    totalPunts: 0,
    puntsRevisats: 0,
    preguntesPreparades: 0,
    alertesDocumentals: 0,
    percentatgeRevisio: 0,
    puntsIds: [],
    notes: '',
  },
  // ── Berga AC (ws-muni-001-ac) ─────────────────────────────────────
  {
    id: 'ple-ac-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-ac',
    tipus: 'ordinari',
    status: 'en_preparacio',
    data: '2026-08-14',
    hora: '19:00',
    lloc: "Sala de Plens de l'Ajuntament de Berga",
    convocatoriaNom: 'Ple Ordinari — Agost 2026 (AC)',
    totalPunts: 2,
    puntsRevisats: 0,
    preguntesPreparades: 1,
    alertesDocumentals: 1,
    percentatgeRevisio: 0,
    puntsIds: ['punt-ac-001', 'punt-ac-002'],
    notes: 'Espai intern Aliança Catalana.',
  },
  // Manresa
  {
    id: 'ple-m-001',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    tipus: 'ordinari',
    status: 'convocatoria_rebuda',
    data: '2026-08-20',
    hora: '18:30',
    lloc: "Sala de Plens de l'Ajuntament de Manresa",
    convocatoriaNom: 'Ple Ordinari — Agost 2026',
    totalPunts: 2,
    puntsRevisats: 0,
    preguntesPreparades: 0,
    alertesDocumentals: 1,
    percentatgeRevisio: 0,
    puntsIds: ['punt-m-001', 'punt-m-002'],
    notes: '',
  },
  {
    id: 'ple-m-002',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    tipus: 'ordinari',
    status: 'tancat',
    data: '2026-07-10',
    hora: '18:30',
    lloc: "Sala de Plens de l'Ajuntament de Manresa",
    convocatoriaNom: 'Ple Ordinari — Juliol 2026',
    totalPunts: 0,
    puntsRevisats: 0,
    preguntesPreparades: 0,
    alertesDocumentals: 0,
    percentatgeRevisio: 100,
    puntsIds: [],
    notes: '',
  },
  // Vic
  {
    id: 'ple-v-001',
    municipiId: 'muni-003',
    workspaceId: 'ws-muni-003',
    tipus: 'ordinari',
    status: 'convocatoria_rebuda',
    data: '2026-09-04',
    hora: '19:00',
    lloc: "Sala de Plens de l'Ajuntament de Vic",
    convocatoriaNom: 'Ple Ordinari — Setembre 2026',
    totalPunts: 1,
    puntsRevisats: 0,
    preguntesPreparades: 0,
    alertesDocumentals: 0,
    percentatgeRevisio: 0,
    puntsIds: ['punt-v-001'],
    notes: '',
  },
];

// ─── Agenda Items ─────────────────────────────────────────────────────

export const mockAgendaItems: AgendaItem[] = [
  // ── Berga General items (ws-muni-001-general) ─────────────────────
  {
    id: 'punt-001',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 1,
    titol: 'Aprovació de l\'acta de la sessió ordinària de juny de 2026',
    categoria: 'aprovacio_acta',
    status: 'revisat',
    prioritat: 'baixa',
    responsable: 'Marta Serra',
    documentIds: ['doc-009'],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Aprovació rutinària de l\'acta de la sessió anterior. Verificar que els acords recollits corresponen als debatuts.',
    textOriginalFragment: 'S\'aprova l\'acta de la sessió ordinària del Ple de l\'Ajuntament de Berga celebrada el dia 12 de juny de 2026, per unanimitat dels membres presents.',
    textOriginalDocumentNom: 'Acta ple ordinari juny 2026',
    textOriginalPagina: 1,
  },
  {
    id: 'punt-002',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 2,
    titol: 'Aprovació de la modificació pressupostària núm. 2/2026 per transferència de crèdit',
    categoria: 'modificacio_pressupostaria',
    status: 'en_revisio',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    documentIds: ['doc-002', 'doc-003'],
    preguntesPreparades: 2,
    alertes: [
      'L\'import de l\'annex econòmic (pàg. 4) difereix en 3.200 € respecte a la proposta principal',
      'Nova versió del document de proposta des del 22/07/2026 — cal revisar els canvis',
    ],
    resum: 'Proposta de modificació del pressupost municipal per transferir crèdit de la partida de manteniment d\'edificis a la de millores en accessibilitat. Import total: 47.800 €.',
    textOriginalFragment: 'Vista la proposta de modificació pressupostària número 2/2026 per transferència de crèdit entre partides, per un import total de quaranta-set mil vuit-cents euros (47.800 €)...',
    textOriginalDocumentNom: 'Proposta modificació pressupostària 2/2026',
    textOriginalPagina: 3,
  },
  {
    id: 'punt-003',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 3,
    titol: 'Aprovació provisional de l\'ordenança reguladora de l\'ocupació de la via pública per terrasses i elements auxiliars',
    categoria: 'ordenanca',
    status: 'en_revisio',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    documentIds: ['doc-004'],
    preguntesPreparades: 1,
    alertes: ['Període d\'exposició pública no consta en els documents disponibles'],
    resum: 'Aprovació provisional de l\'ordenança que regula les condicions per a l\'ocupació de la via pública amb terrasses.',
    textOriginalFragment: 'L\'Ajuntament de Berga, en exercici de la potestat normativa que li confereix l\'article 84 de la Llei 7/1985...',
    textOriginalDocumentNom: 'Ordenança reguladora de terrasses 2026',
    textOriginalPagina: 1,
  },
  {
    id: 'punt-004',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 4,
    titol: 'Aprovació de l\'adjudicació del contracte de servei de neteja viària i recollida de residus 2026–2029',
    categoria: 'adjudicacio',
    status: 'pendent',
    prioritat: 'alta',
    responsable: undefined,
    documentIds: ['doc-005', 'doc-006'],
    preguntesPreparades: 0,
    alertes: [
      'Criteris de valoració tècnica pendents de verificació',
      'Memòria justificativa de la puntuació no consta en els documents disponibles',
    ],
    resum: 'Adjudicació del contracte de serveis de neteja viària i recollida de residus per al període 2026–2029 per un import de 1.240.000 € (IVA inclòs).',
    textOriginalFragment: 'Vista la proposta de resolució de la Mesa de Contractació de data 10 de juliol de 2026...',
    textOriginalDocumentNom: 'Plec de condicions neteja viària 2026–2029',
    textOriginalPagina: 2,
  },
  {
    id: 'punt-005',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 5,
    titol: 'Aprovació de les bases reguladores i la convocatòria de subvencions per a entitats culturals, esportives i socials 2026',
    categoria: 'subvencions',
    status: 'revisat',
    prioritat: 'mitjana',
    responsable: 'Marta Serra',
    documentIds: ['doc-007'],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Aprovació de les bases i convocatòria per a la concessió de subvencions a entitats locals. Dotació total: 85.000 €.',
    textOriginalFragment: 'S\'aproven les bases reguladores per a la concessió de subvencions a entitats culturals, esportives i socials del municipi...',
    textOriginalDocumentNom: 'Bases reguladores subvencions entitats 2026',
    textOriginalPagina: 2,
  },
  {
    id: 'punt-006',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 6,
    titol: 'Aprovació del conveni de col·laboració amb la Diputació de Barcelona per al Pla de Barris 2026–2028',
    categoria: 'conveni',
    status: 'revisat',
    prioritat: 'mitjana',
    responsable: 'Marta Serra',
    documentIds: ['doc-008'],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Conveni amb la Diputació de Barcelona per al finançament d\'actuacions del Pla de Barris. Aportació Diputació: 320.000 €. Aportació municipal: 80.000 €.',
    textOriginalFragment: 'S\'aprova el conveni de col·laboració entre l\'Ajuntament de Berga i la Diputació de Barcelona...',
    textOriginalDocumentNom: 'Conveni col·laboració Diputació de Barcelona',
    textOriginalPagina: 1,
  },
  {
    id: 'punt-007',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 7,
    titol: 'Aprovació del contracte menor de manteniment de les instal·lacions esportives municipals 2026–2027',
    categoria: 'manteniment',
    status: 'pendent',
    prioritat: 'mitjana',
    responsable: undefined,
    documentIds: ['doc-010'],
    preguntesPreparades: 0,
    alertes: ['Informe tècnic complementari no consta en els documents disponibles'],
    resum: 'Contracte menor per al manteniment de les instal·lacions esportives municipals per un import de 38.400 € per a 2026–2027.',
    textOriginalFragment: 'Vista la memòria justificativa de data 9 de juliol de 2026 elaborada pel Servei d\'Esports...',
    textOriginalDocumentNom: 'Memòria manteniment instal·lacions esportives 2025',
    textOriginalPagina: 3,
  },
  {
    id: 'punt-008',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 8,
    titol: 'Donar compte de les resolucions d\'alcaldia del mes de juliol de 2026',
    categoria: 'informatiu',
    status: 'pendent',
    prioritat: 'baixa',
    responsable: undefined,
    documentIds: [],
    preguntesPreparades: 0,
    alertes: ['Documents de resolucions no adjunts a la convocatòria'],
    resum: 'Punt informatiu de caràcter obligatori. Cal verificar que les resolucions d\'alcaldia del mes de juliol han estat correctament notificades i registrades.',
  },
  {
    id: 'punt-009',
    plenarySessionId: 'ple-001',
    workspaceId: 'ws-muni-001-general',
    numero: 9,
    titol: 'Precs, preguntes i interpel·lacions',
    categoria: 'precs_preguntes',
    status: 'amb_preguntes',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    documentIds: [],
    preguntesPreparades: 3,
    alertes: [],
    resum: 'Punt reservat per a la presentació de precs, preguntes i interpel·lacions per part dels grups municipals.',
  },
  // ── Berga AC items (ws-muni-001-ac) ──────────────────────────────
  {
    id: 'punt-ac-001',
    plenarySessionId: 'ple-ac-001',
    workspaceId: 'ws-muni-001-ac',
    numero: 1,
    titol: 'Seguiment del pla d\'acció municipal — Aliança Catalana',
    categoria: 'informatiu',
    status: 'en_revisio',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    documentIds: ['doc-ac-002'],
    preguntesPreparades: 1,
    alertes: ['Informe de seguiment pendent de validació interna'],
    resum: 'Revisió interna del pla d\'acció municipal per al segon semestre de 2026.',
  },
  {
    id: 'punt-ac-002',
    plenarySessionId: 'ple-ac-001',
    workspaceId: 'ws-muni-001-ac',
    numero: 2,
    titol: 'Precs i preguntes — Espai intern AC',
    categoria: 'precs_preguntes',
    status: 'pendent',
    prioritat: 'mitjana',
    responsable: 'Marta Serra',
    documentIds: [],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Punt reservat per a precs i preguntes de l\'espai intern d\'Aliança Catalana.',
  },
  // Manresa items
  {
    id: 'punt-m-001',
    plenarySessionId: 'ple-m-001',
    workspaceId: 'ws-muni-002',
    numero: 1,
    titol: 'Aprovació de l\'acta de la sessió ordinària de juliol de 2026',
    categoria: 'aprovacio_acta',
    status: 'pendent',
    prioritat: 'baixa',
    responsable: undefined,
    documentIds: [],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Aprovació rutinària de l\'acta de la sessió anterior.',
  },
  {
    id: 'punt-m-002',
    plenarySessionId: 'ple-m-001',
    workspaceId: 'ws-muni-002',
    numero: 2,
    titol: 'Aprovació del Pla Local de Joventut 2026–2030',
    categoria: 'altres',
    status: 'pendent',
    prioritat: 'mitjana',
    responsable: undefined,
    documentIds: ['doc-m-001'],
    preguntesPreparades: 0,
    alertes: ['Memòria participativa pendent d\'adjuntar'],
    resum: 'Aprovació del Pla Local de Joventut que estableix les línies estratègiques de les polítiques de joventut per al període 2026–2030.',
  },
  // Vic items
  {
    id: 'punt-v-001',
    plenarySessionId: 'ple-v-001',
    workspaceId: 'ws-muni-003',
    numero: 1,
    titol: 'Aprovació del Reglament de Participació Ciutadana',
    categoria: 'ordenanca',
    status: 'pendent',
    prioritat: 'alta',
    responsable: undefined,
    documentIds: ['doc-v-001'],
    preguntesPreparades: 0,
    alertes: [],
    resum: 'Aprovació del nou Reglament de Participació Ciutadana que regula els mecanismes de participació directa de la ciutadania en la gestió municipal.',
  },
];

// ─── Citations ────────────────────────────────────────────────────────

export const mockCitations: Citation[] = [
  {
    id: 'cit-001',
    agendaItemId: 'punt-002',
    documentId: 'doc-002',
    etiqueta: 'Proposta, pàg. 3',
    pagina: 3,
    fragment: 'import total de quaranta-set mil vuit-cents euros (47.800 €)',
    fragmentHighlight: 'quaranta-set mil vuit-cents euros (47.800 €)',
    verificacioEstat: 'verificat',
    nota: 'Import confirmat al text principal de la proposta.',
  },
  {
    id: 'cit-002',
    agendaItemId: 'punt-002',
    documentId: 'doc-003',
    etiqueta: 'Annex econòmic, pàg. 4',
    pagina: 4,
    fragment: 'transferència de crèdit per import de quaranta-quatre mil sis-cents euros (44.600 €)',
    fragmentHighlight: 'quaranta-quatre mil sis-cents euros (44.600 €)',
    verificacioEstat: 'pendent_verificacio',
    nota: 'Import difereix en 3.200 € respecte a la proposta principal. Cal comprovar quin és el correcte.',
  },
  {
    id: 'cit-003',
    agendaItemId: 'punt-002',
    documentId: 'doc-002',
    etiqueta: 'Proposta, pàg. 5',
    pagina: 5,
    fragment: 'partida de manteniment d\'edificis municipals (151.212.00) al programa de millores en accessibilitat (151.632.00)',
    fragmentHighlight: 'manteniment d\'edificis municipals (151.212.00) al programa de millores en accessibilitat (151.632.00)',
    verificacioEstat: 'verificat',
    nota: 'Partides pressupostàries confirmades.',
  },
  {
    id: 'cit-004',
    agendaItemId: 'punt-004',
    documentId: 'doc-006',
    etiqueta: 'Informe tècnic, pàg. 2',
    pagina: 2,
    fragment: 'ha obtingut la puntuació tècnica de 78,4 sobre 100 punts',
    fragmentHighlight: 'puntuació tècnica de 78,4 sobre 100 punts',
    verificacioEstat: 'pendent_verificacio',
    nota: 'Cal verificar criteris de valoració tècnica aplicats.',
  },
  {
    id: 'cit-005',
    agendaItemId: 'punt-004',
    documentId: 'doc-005',
    etiqueta: 'Plec de condicions, pàg. 8',
    pagina: 8,
    apartat: 'Apartat 4.2 — Criteris de valoració',
    fragment: 'Els criteris de valoració tècnica tindran un pes del 60% sobre la puntuació total, i els criteris econòmics el 40% restant.',
    fragmentHighlight: 'criteris de valoració tècnica tindran un pes del 60%',
    verificacioEstat: 'verificat',
  },
  {
    id: 'cit-006',
    agendaItemId: 'punt-004',
    documentId: 'doc-005',
    etiqueta: 'Plec de condicions, pàg. 12',
    pagina: 12,
    fragment: 'El contractista haurà de disposar d\'un mínim de 8 vehicles de recollida i 2 màquines escombradores.',
    fragmentHighlight: 'mínim de 8 vehicles de recollida i 2 màquines escombradores',
    verificacioEstat: 'verificat',
  },
];

// ─── Extracted Data Blocks ────────────────────────────────────────────

export const mockExtractedData: ExtractedDataBlock[] = [
  {
    id: 'dex-001',
    agendaItemId: 'punt-002',
    tipus: 'resum',
    etiqueta: 'Resum',
    valor: 'Modificació pressupostària per transferència de crèdit entre partides. Import: 47.800 € (proposta) / 44.600 € (annex econòmic — discrepància pendent de verificació).',
    verificacioEstat: 'pendent_verificacio',
    citacioIds: ['cit-001', 'cit-002'],
  },
  {
    id: 'dex-002',
    agendaItemId: 'punt-002',
    tipus: 'imports',
    etiqueta: 'Imports',
    valor: ['47.800 € (Proposta principal, pàg. 3)', '44.600 € (Annex econòmic, pàg. 4) ⚠ Discrepància'],
    verificacioEstat: 'pendent_verificacio',
    citacioIds: ['cit-001', 'cit-002'],
    nota: 'Els dos documents presenten imports diferents. Cal comprovar quin és el correcte abans del ple.',
  },
  {
    id: 'dex-003',
    agendaItemId: 'punt-002',
    tipus: 'linia_pressupostaria',
    etiqueta: 'Línies pressupostàries',
    valor: ['Origen: 151.212.00 — Manteniment d\'edificis municipals', 'Destinació: 151.632.00 — Millores en accessibilitat'],
    verificacioEstat: 'verificat',
    citacioIds: ['cit-003'],
  },
  {
    id: 'dex-004',
    agendaItemId: 'punt-002',
    tipus: 'acord_proposat',
    etiqueta: 'Acord proposat',
    valor: 'Aprovar la modificació pressupostària núm. 2/2026 per transferència de crèdit entre les partides 151.212.00 i 151.632.00 per l\'import que resulti de la verificació.',
    verificacioEstat: 'inferencia',
    nota: 'Inferència del sistema — Pendent de revisió humana.',
  },
  {
    id: 'dex-005',
    agendaItemId: 'punt-004',
    tipus: 'resum',
    etiqueta: 'Resum',
    valor: 'Adjudicació del contracte de neteja viària 2026–2029 per 1.240.000 € (IVA inclòs). Puntuació tècnica: 78,4/100.',
    verificacioEstat: 'verificat',
    citacioIds: ['cit-004', 'cit-005'],
  },
  {
    id: 'dex-006',
    agendaItemId: 'punt-004',
    tipus: 'actors',
    etiqueta: 'Actors i organitzacions',
    valor: ['Empresa adjudicatària (NIF: B-08234567)', 'Mesa de Contractació — òrgan de valoració'],
    verificacioEstat: 'verificat',
    citacioIds: ['cit-004'],
  },
  {
    id: 'dex-007',
    agendaItemId: 'punt-004',
    tipus: 'imports',
    etiqueta: 'Import total',
    valor: '1.240.000 € (IVA inclòs) — Període 2026–2029',
    verificacioEstat: 'verificat',
    citacioIds: ['cit-004'],
  },
  {
    id: 'dex-008',
    agendaItemId: 'punt-004',
    tipus: 'durada',
    etiqueta: 'Durada del contracte',
    valor: '3 anys (2026–2029), amb possibilitat de pròrroga de 2 anys addicionals',
    verificacioEstat: 'inferencia',
    nota: 'Inferència del sistema — Pendent de revisió humana.',
  },
];

// ─── Review Items ─────────────────────────────────────────────────────

export const mockReviewItems: ReviewItem[] = [
  {
    id: 'rev-001',
    agendaItemId: 'punt-002',
    tipus: 'discrepancia_import',
    descripcio: 'L\'import de la proposta principal (47.800 €) difereix de l\'import de l\'annex econòmic (44.600 €) en 3.200 €.',
    estat: 'obert',
  },
  {
    id: 'rev-002',
    agendaItemId: 'punt-002',
    tipus: 'confirmacio_necessaria',
    descripcio: 'La versió 1.1 de la proposta (actualitzada el 22/07/2026) pot contenir modificacions rellevants.',
    estat: 'obert',
  },
  {
    id: 'rev-003',
    agendaItemId: 'punt-004',
    tipus: 'annex_mancat',
    descripcio: 'La memòria justificativa de la puntuació tècnica de les ofertes no consta en els documents disponibles.',
    estat: 'obert',
  },
  {
    id: 'rev-004',
    agendaItemId: 'punt-004',
    tipus: 'confirmacio_necessaria',
    descripcio: 'Cal verificar que els criteris de valoració tècnica aplicats (60/40) corresponen als establerts al plec de condicions aprovat.',
    estat: 'obert',
  },
  {
    id: 'rev-005',
    agendaItemId: 'punt-003',
    tipus: 'informacio_addicional',
    descripcio: 'El període d\'exposició pública prèvia a l\'aprovació provisional no consta en els documents disponibles.',
    estat: 'obert',
  },
  {
    id: 'rev-006',
    agendaItemId: 'punt-007',
    tipus: 'informe_mancat',
    descripcio: 'L\'informe tècnic complementari sobre l\'estat de les instal·lacions esportives no consta en els documents disponibles.',
    estat: 'obert',
  },
];

// ─── Suggested Questions ──────────────────────────────────────────────

export const mockSuggestedQuestions: SuggestedQuestion[] = [
  {
    id: 'sq-001',
    agendaItemId: 'punt-002',
    text: 'Quin és l\'import definitiu de la modificació pressupostària: els 47.800 € de la proposta principal o els 44.600 € de l\'annex econòmic?',
    objectiu: 'Clarificar la discrepància d\'import entre documents per garantir la transparència de la modificació pressupostària.',
    fontCitacioId: 'cit-002',
    fontDocumentNom: 'Annex econòmic, pàg. 4',
    convertitEnEsborrany: false,
  },
  {
    id: 'sq-002',
    agendaItemId: 'punt-002',
    text: 'Quins criteris tècnics justifiquen la transferència de crèdit de la partida de manteniment d\'edificis a la de millores en accessibilitat?',
    objectiu: 'Conèixer la justificació tècnica de la transferència i verificar que existeix documentació de suport adequada.',
    fontCitacioId: 'cit-003',
    fontDocumentNom: 'Proposta, pàg. 5',
    convertitEnEsborrany: true,
    draftId: 'draft-001',
  },
  {
    id: 'sq-003',
    agendaItemId: 'punt-004',
    text: 'Pot facilitar el govern municipal la memòria de valoració tècnica de les ofertes presentades, incloent-hi la puntuació desglossada de cada empresa licitadora?',
    objectiu: 'Garantir la transparència del procés d\'adjudicació.',
    fontCitacioId: 'cit-004',
    fontDocumentNom: 'Informe tècnic, pàg. 2',
    convertitEnEsborrany: false,
  },
  {
    id: 'sq-004',
    agendaItemId: 'punt-004',
    text: 'Quantes empreses es van presentar a la licitació del contracte de neteja viària?',
    objectiu: 'Conèixer el nivell de concurrència del procés i la competitivitat de l\'oferta adjudicada.',
    fontCitacioId: 'cit-005',
    fontDocumentNom: 'Plec de condicions, pàg. 8',
    convertitEnEsborrany: false,
  },
  {
    id: 'sq-005',
    agendaItemId: 'punt-003',
    text: 'En quin moment s\'ha realitzat el tràmit d\'exposició pública de l\'ordenança de terrasses?',
    objectiu: 'Verificar el compliment del procediment normatiu.',
    fontDocumentNom: 'Ordenança reguladora de terrasses 2026',
    convertitEnEsborrany: false,
  },
];

// ─── Issues ───────────────────────────────────────────────────────────

export const mockIssues: Issue[] = [
  // ── Berga General issues (ws-muni-001-general) ────────────────────
  {
    id: 'issue-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Discrepàncies en l\'import de la modificació pressupostària 2/2026',
    descripcio: 'S\'han detectat diferències de 3.200 € entre la proposta principal i l\'annex econòmic de la modificació pressupostària.',
    categoria: 'modificacio_pressupostaria',
    status: 'en_estudi',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-23',
    dataLimit: '2026-08-13',
    proximaAccio: 'Presentar pregunta al ple del 14 d\'agost',
    documentIds: ['doc-002', 'doc-003'],
    plenarySessionIds: ['ple-001'],
    agendaItemIds: ['punt-002'],
    draftIds: ['draft-001'],
    etiquetes: ['pressupost', 'modificació', 'discrepància'],
  },
  {
    id: 'issue-002',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Transparència en l\'adjudicació del contracte de neteja viària',
    descripcio: 'Cal obtenir la memòria de valoració tècnica completa de les ofertes i verificar que els criteris d\'adjudicació s\'han aplicat correctament.',
    categoria: 'adjudicacio',
    status: 'en_actuacio',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-20',
    dataLimit: '2026-08-14',
    proximaAccio: 'Presentar pregunta sobre criteris de valoració al ple',
    documentIds: ['doc-005', 'doc-006'],
    plenarySessionIds: ['ple-001'],
    agendaItemIds: ['punt-004'],
    draftIds: ['draft-002'],
    etiquetes: ['contractació', 'neteja', 'transparència'],
  },
  {
    id: 'issue-003',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Manteniment de les pistes de pàdel del Pavelló Municipal',
    descripcio: 'Diverses queixes veïnals sobre l\'estat de les pistes de pàdel.',
    categoria: 'esports',
    status: 'esperant_resposta',
    prioritat: 'mitjana',
    responsable: 'Marta Serra',
    dataCreacio: '2026-06-15',
    dataLimit: '2026-09-01',
    proximaAccio: 'Seguiment de la resposta del govern',
    documentIds: [],
    plenarySessionIds: ['ple-002'],
    agendaItemIds: [],
    draftIds: ['draft-003'],
    etiquetes: ['esports', 'manteniment', 'pavelló'],
  },
  {
    id: 'issue-004',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Accessibilitat al carrer de la Pietat — vorera trencada',
    descripcio: 'La vorera del tram central del carrer de la Pietat presenta discontinuïtats que impedeixen el pas de cadires de rodes i cotxets.',
    categoria: 'urbanisme',
    status: 'nou',
    prioritat: 'alta',
    responsable: undefined,
    dataCreacio: '2026-07-28',
    dataLimit: undefined,
    proximaAccio: 'Assignar responsable i documentar l\'estat actual',
    documentIds: [],
    plenarySessionIds: [],
    agendaItemIds: [],
    draftIds: [],
    etiquetes: ['accessibilitat', 'urbanisme', 'vorera'],
  },
  {
    id: 'issue-005',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Pla de Barris — seguiment de les actuacions al barri del Roser',
    descripcio: 'Supervisió de les actuacions previstes al conveni amb la Diputació de Barcelona per al Pla de Barris.',
    categoria: 'general',
    status: 'en_estudi',
    prioritat: 'mitjana',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-15',
    dataLimit: '2026-12-31',
    proximaAccio: 'Sol·licitar pla d\'execució detallat',
    documentIds: ['doc-008'],
    plenarySessionIds: ['ple-001'],
    agendaItemIds: ['punt-006'],
    draftIds: [],
    etiquetes: ['pla-de-barris', 'roser', 'diputació'],
  },
  {
    id: 'issue-006',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Il·luminació deficient al Parc de l\'Estació durant les nits',
    descripcio: 'Diverses queixes de veïns sobre la manca d\'il·luminació adequada al Parc de l\'Estació durant les hores nocturnes.',
    categoria: 'seguretat',
    status: 'resolt',
    prioritat: 'baixa',
    responsable: 'Marta Serra',
    dataCreacio: '2026-05-10',
    dataLimit: '2026-06-30',
    proximaAccio: undefined,
    documentIds: [],
    plenarySessionIds: ['ple-002'],
    agendaItemIds: [],
    draftIds: [],
    etiquetes: ['il·luminació', 'seguretat', 'parc'],
  },
  // ── Berga AC issues (ws-muni-001-ac) ─────────────────────────────
  {
    id: 'issue-ac-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-ac',
    titol: 'Seguiment del pla d\'acció intern — Aliança Catalana Berga',
    descripcio: 'Revisió interna de les actuacions previstes per al segon semestre de 2026 dins de l\'espai AC.',
    categoria: 'general',
    status: 'en_estudi',
    prioritat: 'alta',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-15',
    dataLimit: '2026-12-31',
    proximaAccio: 'Reunió de seguiment interna',
    documentIds: ['doc-ac-002'],
    plenarySessionIds: ['ple-ac-001'],
    agendaItemIds: ['punt-ac-001'],
    draftIds: ['draft-ac-001'],
    etiquetes: ['intern', 'ac', 'seguiment'],
  },
  // Manresa issues
  {
    id: 'issue-m-001',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    titol: 'Revisió del Pla d\'Ordenació Urbanística Municipal',
    descripcio: 'Seguiment de la revisió del POUM en curs. Cal verificar els terminis de participació pública.',
    categoria: 'urbanisme',
    status: 'en_estudi',
    prioritat: 'alta',
    responsable: undefined,
    dataCreacio: '2026-06-01',
    dataLimit: '2026-12-31',
    proximaAccio: 'Consultar l\'estat del tràmit d\'informació pública',
    documentIds: [],
    plenarySessionIds: [],
    agendaItemIds: [],
    draftIds: [],
    etiquetes: ['urbanisme', 'POUM', 'planificació'],
  },
  {
    id: 'issue-m-002',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    titol: 'Millora de la xarxa de carril bici al centre urbà',
    descripcio: 'Proposta per ampliar i connectar els trams de carril bici existents al centre de la ciutat.',
    categoria: 'urbanisme',
    status: 'nou',
    prioritat: 'mitjana',
    responsable: undefined,
    dataCreacio: '2026-07-10',
    dataLimit: undefined,
    proximaAccio: 'Elaborar proposta tècnica inicial',
    documentIds: [],
    plenarySessionIds: [],
    agendaItemIds: [],
    draftIds: [],
    etiquetes: ['mobilitat', 'carril-bici', 'sostenibilitat'],
  },
  // Vic issues
  {
    id: 'issue-v-001',
    municipiId: 'muni-003',
    workspaceId: 'ws-muni-003',
    titol: 'Seguiment del Pla de Mobilitat Urbana Sostenible',
    descripcio: 'Revisió de l\'estat d\'execució del PMUS aprovat el 2024 i verificació dels indicadors de seguiment.',
    categoria: 'general',
    status: 'en_estudi',
    prioritat: 'mitjana',
    responsable: undefined,
    dataCreacio: '2026-07-01',
    dataLimit: '2026-09-30',
    proximaAccio: 'Sol·licitar informe de seguiment als serveis tècnics',
    documentIds: [],
    plenarySessionIds: [],
    agendaItemIds: [],
    draftIds: [],
    etiquetes: ['mobilitat', 'sostenibilitat', 'PMUS'],
  },
];

// ─── Drafts ───────────────────────────────────────────────────────────

export const mockDrafts: Draft[] = [
  // ── Berga General drafts (ws-muni-001-general) ───────────────────
  {
    id: 'draft-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Pregunta sobre la justificació tècnica de la modificació pressupostària 2/2026',
    tipus: 'pregunta_escrita',
    status: 'pendent_revisio',
    destinatari: 'Alcaldia',
    antecedents: 'En el punt 2 de l\'ordre del dia del ple ordinari d\'agost de 2026 es proposa l\'aprovació d\'una modificació pressupostària per transferència de crèdit per un import que difereix entre la proposta principal (47.800 €) i l\'annex econòmic (44.600 €).',
    cossPrincipal: 'El Grup Municipal, en virtut del dret d\'informació que assisteix als membres de la corporació, formula la següent pregunta:\n\nQuins criteris tècnics justifiquen la transferència de crèdit de la partida de manteniment d\'edificis (151.212.00) a la de millores en accessibilitat (151.632.00) per import de 47.800 €?\n\nExisteix un informe tècnic previ que avali la necessitat i l\'adequació d\'aquesta modificació pressupostària?',
    preguntesComplementaries: [
      'Quina és la causa de la discrepància entre l\'import de la proposta principal (47.800 €) i el de l\'annex econòmic (44.600 €)?',
      'Quin dels dos documents és el definitiu i quin serà l\'import exacte de la modificació que se sotmet a votació?',
    ],
    fontIds: ['doc-002', 'doc-003'],
    notesInternes: 'Revisar amb la resta del grup abans de presentar.',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-24',
    dataModificacio: '2026-07-26',
    issueId: 'issue-001',
    agendaItemId: 'punt-002',
    plenarySessionId: 'ple-001',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-24', autor: 'Marta Serra', canvis: 'Creació inicial de l\'esborrany' },
      { versio: '1.1', data: '2026-07-26', autor: 'Marta Serra', canvis: 'Afegides preguntes complementàries sobre la discrepància d\'import' },
    ],
  },
  {
    id: 'draft-002',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Petició d\'informació sobre criteris d\'adjudicació — Contracte neteja viària 2026–2029',
    tipus: 'peticio_informacio',
    status: 'esborrany',
    destinatari: 'Regidoria de Serveis Urbans',
    antecedents: 'El punt 4 de l\'ordre del dia proposa l\'adjudicació del contracte de neteja viària. La memòria de valoració tècnica de les ofertes no s\'ha adjuntat a la documentació del ple.',
    cossPrincipal: 'El Grup Municipal sol·licita la documentació completa del procés de licitació del contracte de servei de neteja viària i recollida de residus 2026–2029.',
    fontIds: ['doc-005', 'doc-006'],
    notesInternes: '',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-22',
    dataModificacio: '2026-07-22',
    issueId: 'issue-002',
    agendaItemId: 'punt-004',
    plenarySessionId: 'ple-001',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-22', autor: 'Marta Serra', canvis: 'Creació inicial' },
    ],
  },
  {
    id: 'draft-003',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Prec sobre el manteniment de les pistes de pàdel del Pavelló Municipal',
    tipus: 'prec',
    status: 'presentat',
    destinatari: 'Regidoria d\'Esports',
    antecedents: 'Diverses persones usuàries del Pavelló Municipal han traslladat les seves queixes sobre l\'estat deteriorat de les pistes de pàdel.',
    cossPrincipal: 'El Grup Municipal prega al govern municipal que procedeixi a la revisió integral de les pistes de pàdel del Pavelló Municipal.',
    fontIds: [],
    notesInternes: 'Presentat al ple de juny. Esperant resposta del govern.',
    responsable: 'Marta Serra',
    dataCreacio: '2026-06-05',
    dataModificacio: '2026-06-10',
    issueId: 'issue-003',
    plenarySessionId: 'ple-002',
    versioHistorial: [
      { versio: '1.0', data: '2026-06-05', autor: 'Marta Serra', canvis: 'Creació' },
      { versio: '1.1', data: '2026-06-10', autor: 'Marta Serra', canvis: 'Revisió final abans de presentació' },
    ],
  },
  {
    id: 'draft-004',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Pregunta oral sobre el calendari d\'actuacions del Pla de Barris al Roser',
    tipus: 'pregunta_oral',
    status: 'validat',
    destinatari: 'Alcaldia',
    antecedents: 'El ple d\'agost de 2026 aprovarà el conveni amb la Diputació de Barcelona per al Pla de Barris.',
    cossPrincipal: 'Quina és la planificació concreta de les actuacions del Pla de Barris al barri del Roser per al període 2026–2028?',
    fontIds: ['doc-008'],
    notesInternes: 'Validat. Llest per presentar al ple d\'agost.',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-25',
    dataModificacio: '2026-07-30',
    plenarySessionId: 'ple-001',
    agendaItemId: 'punt-006',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-25', autor: 'Marta Serra', canvis: 'Creació' },
      { versio: '1.1', data: '2026-07-30', autor: 'Marta Serra', canvis: 'Revisió i validació' },
    ],
  },
  {
    id: 'draft-005',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    titol: 'Moció per a la millora de l\'accessibilitat en vies públiques del centre urbà',
    tipus: 'mocio',
    status: 'esborrany',
    destinatari: 'Ple de l\'Ajuntament',
    antecedents: 'S\'han detectat múltiples punts del centre urbà amb problemes d\'accessibilitat per a persones amb mobilitat reduïda.',
    cossPrincipal: 'El Grup Municipal proposa al Ple l\'adopció dels acords per a l\'elaboració d\'un diagnòstic integral de les barreres arquitectòniques existents a la via pública del centre urbà.',
    fontIds: [],
    notesInternes: 'Esborrany inicial. Pendent de revisar amb la resta del grup.',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-29',
    dataModificacio: '2026-07-29',
    issueId: 'issue-004',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-29', autor: 'Marta Serra', canvis: 'Creació inicial' },
    ],
  },
  // ── Berga AC drafts (ws-muni-001-ac) ─────────────────────────────
  {
    id: 'draft-ac-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-ac',
    titol: 'Pregunta interna sobre el seguiment del pla d\'acció AC',
    tipus: 'pregunta_escrita' as const,
    status: 'esborrany' as const,
    destinatari: 'Coordinació interna AC',
    antecedents: 'Revisió del pla d\'acció intern de l\'espai AC de Berga per al segon semestre de 2026.',
    cossPrincipal: 'Quines actuacions del pla d\'acció intern estan pendents de completar per al segon semestre de 2026?',
    fontIds: ['doc-ac-002'],
    notesInternes: 'Esborrany intern — no publicar.',
    responsable: 'Marta Serra',
    dataCreacio: '2026-07-20',
    dataModificacio: '2026-07-20',
    issueId: 'issue-ac-001',
    plenarySessionId: 'ple-ac-001',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-20', autor: 'Marta Serra', canvis: 'Creació inicial' },
    ],
  },
  // Manresa drafts
  {
    id: 'draft-m-001',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    titol: 'Pregunta sobre els terminis de participació pública del POUM',
    tipus: 'pregunta_escrita',
    status: 'esborrany',
    destinatari: 'Regidoria d\'Urbanisme',
    antecedents: 'La revisió del POUM es troba en fase de tramitació. Cal conèixer els terminis exactes per a la participació pública.',
    cossPrincipal: 'Quins són els terminis previstos per al tràmit d\'informació pública de la revisió del POUM? Quins mecanismes de participació ciutadana s\'han previst?',
    fontIds: [],
    notesInternes: '',
    responsable: 'Usuari de demostració',
    dataCreacio: '2026-07-15',
    dataModificacio: '2026-07-15',
    issueId: 'issue-m-001',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-15', autor: 'Usuari de demostració', canvis: 'Creació inicial' },
    ],
  },
  // Vic drafts
  {
    id: 'draft-v-001',
    municipiId: 'muni-003',
    workspaceId: 'ws-muni-003',
    titol: 'Prec sobre l\'informe de seguiment del PMUS',
    tipus: 'prec',
    status: 'esborrany',
    destinatari: 'Regidoria de Mobilitat',
    antecedents: 'El Pla de Mobilitat Urbana Sostenible (PMUS) va ser aprovat el 2024. Cal verificar l\'estat d\'execució de les mesures previstes.',
    cossPrincipal: 'El Grup Municipal prega al govern municipal que presenti un informe de seguiment del PMUS 2024 amb l\'estat d\'execució de cada mesura i els indicadors de compliment.',
    fontIds: [],
    notesInternes: '',
    responsable: 'Usuari de demostració',
    dataCreacio: '2026-07-20',
    dataModificacio: '2026-07-20',
    issueId: 'issue-v-001',
    versioHistorial: [
      { versio: '1.0', data: '2026-07-20', autor: 'Usuari de demostració', canvis: 'Creació inicial' },
    ],
  },
];

// ─── Activity Events ───────────────────────────────────────────────────

export const mockActivityEvents: ActivityEvent[] = [
  // ── Berga General activity (ws-muni-001-general) ──────────────────
  {
    id: 'act-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'document_nou',
    descripcio: 'Nova convocatòria del Ple Ordinari de Setembre 2026 disponible',
    data: '2026-07-28T10:30:00',
    entitat: 'Ple Ordinari — Setembre 2026',
    entitatId: 'ple-003',
    entitatTipus: 'plenary',
    llegit: false,
  },
  {
    id: 'act-002',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'annex_actualitzat',
    descripcio: 'Nova versió (1.1) de la proposta de modificació pressupostària 2/2026',
    data: '2026-07-22T16:45:00',
    entitat: 'Proposta modificació pressupostària 2/2026',
    entitatId: 'doc-002',
    entitatTipus: 'document',
    llegit: false,
  },
  {
    id: 'act-003',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'assumpte_pendent',
    descripcio: 'Assumpte "Accessibilitat al carrer de la Pietat" sense responsable assignat',
    data: '2026-07-28T09:15:00',
    entitat: 'Accessibilitat al carrer de la Pietat',
    entitatId: 'issue-004',
    entitatTipus: 'issue',
    llegit: false,
  },
  {
    id: 'act-004',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'esborrany_modificat',
    descripcio: 'Esborrany "Pregunta sobre la justificació tècnica..." actualitzat',
    data: '2026-07-26T11:20:00',
    entitat: 'Pregunta sobre la justificació tècnica de la modificació pressupostària',
    entitatId: 'draft-001',
    entitatTipus: 'draft',
    llegit: true,
  },
  {
    id: 'act-005',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-general',
    tipus: 'punt_revisat',
    descripcio: 'Punt 5 "Subvencions entitats 2026" marcat com a revisat',
    data: '2026-07-25T14:30:00',
    entitat: 'Subvencions entitats 2026',
    entitatId: 'punt-005',
    entitatTipus: 'agendaItem',
    llegit: true,
  },
  // ── Berga AC activity (ws-muni-001-ac) ───────────────────────────
  {
    id: 'act-ac-001',
    municipiId: 'muni-001',
    workspaceId: 'ws-muni-001-ac',
    tipus: 'document_nou',
    descripcio: 'Informe intern de seguiment disponible — Aliança Catalana Berga',
    data: '2026-07-20T10:00:00',
    entitat: 'Informe intern — Seguiment actuacions municipals',
    entitatId: 'doc-ac-002',
    entitatTipus: 'document',
    llegit: false,
  },
  // Manresa activity
  {
    id: 'act-m-001',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    tipus: 'document_nou',
    descripcio: 'Nova convocatòria del Ple Ordinari d\'Agost 2026 disponible',
    data: '2026-07-25T09:00:00',
    entitat: 'Ple Ordinari — Agost 2026',
    entitatId: 'ple-m-001',
    entitatTipus: 'plenary',
    llegit: false,
  },
  {
    id: 'act-m-002',
    municipiId: 'muni-002',
    workspaceId: 'ws-muni-002',
    tipus: 'esborrany_modificat',
    descripcio: 'Esborrany "Pregunta sobre els terminis del POUM" actualitzat',
    data: '2026-07-15T11:00:00',
    entitat: 'Pregunta sobre els terminis de participació pública del POUM',
    entitatId: 'draft-m-001',
    entitatTipus: 'draft',
    llegit: true,
  },
  // Vic activity
  {
    id: 'act-v-001',
    municipiId: 'muni-003',
    workspaceId: 'ws-muni-003',
    tipus: 'document_nou',
    descripcio: 'Nova convocatòria del Ple Ordinari de Setembre 2026 disponible',
    data: '2026-08-01T10:00:00',
    entitat: 'Ple Ordinari — Setembre 2026',
    entitatId: 'ple-v-001',
    entitatTipus: 'plenary',
    llegit: false,
  },
  {
    id: 'act-v-002',
    municipiId: 'muni-003',
    workspaceId: 'ws-muni-003',
    tipus: 'esborrany_modificat',
    descripcio: 'Esborrany "Prec sobre l\'informe de seguiment del PMUS" creat',
    data: '2026-07-20T14:00:00',
    entitat: 'Prec sobre l\'informe de seguiment del PMUS',
    entitatId: 'draft-v-001',
    entitatTipus: 'draft',
    llegit: true,
  },
];

// ─── Alerts ───────────────────────────────────────────────────────────

export const mockAlerts: Alert[] = [
  // Berga alerts
  {
    id: 'alert-001',
    municipiId: 'muni-001',
    tipus: 'versio_nova',
    titol: 'Nova versió del document de proposta pressupostària',
    descripcio: 'La proposta de modificació pressupostària 2/2026 té una nova versió (1.1) des del 22/07/2026. Cal revisar els canvis.',
    data: '2026-07-22T16:45:00',
    prioritat: 'alta',
    llegit: false,
    entitatId: 'doc-002',
    entitatTipus: 'document',
  },
  {
    id: 'alert-002',
    municipiId: 'muni-001',
    tipus: 'document_nou',
    descripcio: 'Nova convocatòria del Ple de Setembre 2026 disponible per a revisió.',
    titol: 'Convocatòria Ple Setembre 2026 disponible',
    data: '2026-07-28T10:30:00',
    prioritat: 'mitjana',
    llegit: false,
    entitatId: 'ple-003',
    entitatTipus: 'plenary',
  },
  {
    id: 'alert-003',
    municipiId: 'muni-001',
    tipus: 'assumpte_pendent',
    titol: 'Assumpte pendent de responsable',
    descripcio: '"Accessibilitat al carrer de la Pietat" no té responsable assignat ni pla d\'acció.',
    data: '2026-07-28T09:15:00',
    prioritat: 'alta',
    llegit: false,
    entitatId: 'issue-004',
    entitatTipus: 'issue',
  },
  {
    id: 'alert-004',
    municipiId: 'muni-001',
    tipus: 'discrepancia',
    titol: 'Discrepància d\'import detectada',
    descripcio: 'L\'annex econòmic de la modificació pressupostària 2/2026 mostra un import diferent a la proposta principal (diferència: 3.200 €).',
    data: '2026-07-23T08:00:00',
    prioritat: 'alta',
    llegit: false,
    entitatId: 'punt-002',
    entitatTipus: 'agendaItem',
  },
  {
    id: 'alert-005',
    municipiId: 'muni-001',
    tipus: 'termini_proper',
    titol: 'Termini proper: Ple Ordinari Agost 2026',
    descripcio: 'Queden pocs dies per al ple del 14 d\'agost. 5 punts pendents de revisió.',
    data: '2026-08-04T07:00:00',
    prioritat: 'alta',
    llegit: false,
    entitatId: 'ple-001',
    entitatTipus: 'plenary',
  },
  // Manresa alerts
  {
    id: 'alert-m-001',
    municipiId: 'muni-002',
    tipus: 'document_nou',
    titol: 'Convocatòria Ple Agost 2026 disponible',
    descripcio: 'Nova convocatòria del Ple Ordinari d\'Agost 2026 de Manresa disponible per a revisió.',
    data: '2026-07-25T09:00:00',
    prioritat: 'mitjana',
    llegit: false,
    entitatId: 'ple-m-001',
    entitatTipus: 'plenary',
  },
  {
    id: 'alert-m-002',
    municipiId: 'muni-002',
    tipus: 'assumpte_pendent',
    titol: 'Revisió del POUM pendent de seguiment',
    descripcio: 'L\'assumpte de revisió del POUM no té responsable assignat.',
    data: '2026-07-20T10:00:00',
    prioritat: 'alta',
    llegit: false,
    entitatId: 'issue-m-001',
    entitatTipus: 'issue',
  },
  // Vic alerts
  {
    id: 'alert-v-001',
    municipiId: 'muni-003',
    tipus: 'document_nou',
    titol: 'Convocatòria Ple Setembre 2026 disponible',
    descripcio: 'Nova convocatòria del Ple Ordinari de Setembre 2026 de Vic disponible per a revisió.',
    data: '2026-08-01T10:00:00',
    prioritat: 'mitjana',
    llegit: false,
    entitatId: 'ple-v-001',
    entitatTipus: 'plenary',
  },
];

// ─── Workspace mock data (for /espais screen) ─────────────────────────

export interface WorkspaceCardData {
  municipalityId: string;
  nom: string;
  comarca: string;
  rol: string;
  proximPle: string | null;
  proximPleData: string | null;
  puntsPendents: number;
  assumptesOberts: number;
  ultimaActivitat: string;
}

const WORKSPACE_ROLES: Record<string, string> = {
  'muni-001': 'Regidora — Grup Municipal Independents',
  'muni-002': 'Regidor — Grup Municipal d\'Oposició',
  'muni-003': 'Regidora — Grup Municipal Independent',
};

function formatRelativeDate(isoDate: string): string {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffHours < 1) return 'Fa menys d\'una hora';
  if (diffHours < 24) return `Fa ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
  if (diffDays === 1) return 'Fa 1 dia';
  return `Fa ${diffDays} dies`;
}

function formatPleDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('ca-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

export function getWorkspaceCardData(): WorkspaceCardData[] {
  return mockMunicipalities.map((muni) => {
    const sessions = mockPlendarySessions.filter((s) => s.municipiId === muni.id);
    const now = new Date();
    const upcomingSessions = sessions
      .filter((s) => new Date(s.data) >= now && (s.status === 'en_preparacio' || s.status === 'convocatoria_rebuda'))
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());
    const properPle = upcomingSessions[0] ?? null;

    const agendaItemsForProperPle = properPle
      ? mockAgendaItems.filter((a) => a.plenarySessionId === properPle.id && a.status === 'pendent')
      : [];
    const puntsPendents = properPle
      ? properPle.totalPunts - properPle.puntsRevisats
      : 0;

    const assumptesOberts = mockIssues.filter(
      (i) => i.municipiId === muni.id && i.status !== 'resolt' && i.status !== 'arxivat'
    ).length;

    const recentActivity = mockActivityEvents
      .filter((a) => a.municipiId === muni.id)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())[0];

    return {
      municipalityId: muni.id,
      nom: muni.nom,
      comarca: muni.comarca,
      rol: WORKSPACE_ROLES[muni.id] ?? 'Regidor/a',
      proximPle: properPle?.convocatoriaNom ?? null,
      proximPleData: properPle ? formatPleDate(properPle.data) : null,
      puntsPendents,
      assumptesOberts,
      ultimaActivitat: recentActivity ? formatRelativeDate(recentActivity.data) : 'Sense activitat recent',
    };
  });
}

// Keep static export for backward compatibility — use getWorkspaceCardData() for dynamic data
export const mockWorkspaces: WorkspaceCardData[] = [
  {
    municipalityId: 'muni-001',
    nom: 'Berga',
    comarca: 'Berguedà',
    rol: 'Regidora — Grup Municipal Independents',
    proximPle: 'Ple Ordinari — Agost 2026',
    proximPleData: '14 d\'agost de 2026',
    puntsPendents: 5,
    assumptesOberts: 4,
    ultimaActivitat: 'Fa 2 hores',
  },
  {
    municipalityId: 'muni-002',
    nom: 'Manresa',
    comarca: 'Bages',
    rol: 'Regidor — Grup Municipal d\'Oposició',
    proximPle: 'Ple Ordinari — Agost 2026',
    proximPleData: '20 d\'agost de 2026',
    puntsPendents: 2,
    assumptesOberts: 2,
    ultimaActivitat: 'Fa 1 dia',
  },
  {
    municipalityId: 'muni-003',
    nom: 'Vic',
    comarca: 'Osona',
    rol: 'Regidora — Grup Municipal Independent',
    proximPle: 'Ple Ordinari — Setembre 2026',
    proximPleData: '4 de setembre de 2026',
    puntsPendents: 1,
    assumptesOberts: 1,
    ultimaActivitat: 'Fa 3 dies',
  },
];